import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router';
import posthog from 'posthog-js';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import {
  fetchUserDetails,
  fetchLocations,
  fetchAllSensorsInLocation,
  fetchAvailableVars,
} from '../services/apiService';
import {
  setUserPublic,
  setLocationData,
  setSensorsById,
  setAvailableVars,
  setCurrentLocation,
} from '../state/actions';
import useStyles from '../styles';

import MobileViewDrawer from '../Widgets/Navigation/MobileViewDrawer';
import DesktopViewDrawer from '../Widgets/Navigation/DesktopViewDrawer';
import MainWrapper from './MainWrapper';
import { SensorLatest } from '../services/api';
import { retrieveState, StorageTypes } from '../utils/persistentState';
import { useAppDispatch, useAppSelector } from '../state/store';
import { routes } from './helpers';
import { getCurrentLocation, getLocationsById } from '../state/selectors';
import { themeProps } from '../styles/theme';

interface ShellProps {
  userName: string;
}

function Shell({ userName }: ShellProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { classes } = useStyles();
  const locationsById = useAppSelector(getLocationsById);
  const currentLocation = useAppSelector(getCurrentLocation);

  // temporary banner for TFL subscriptions
  const [showSubscriptionBanner, setShowSubscriptionBanner] = useState(false);
  const allTFLLocations = [
    'Transport for London',
    '14 Pier Walk',
    '200 Buckingham Palace Rd',
    '5 Endeavour Square',
    'Palestra House',
  ];

  useEffect(() => {
    const locName = locationsById.get('#')?.name ?? '';
    if (locName && allTFLLocations.includes(locName)) setShowSubscriptionBanner(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLocation, locationsById]);

  useEffect(() => {
    if (userName) {
      fetchUserDetails()
        .then((userDetails) => {
          dispatch(setUserPublic(userDetails));
        })
        .catch(() => {
          dispatch(setUserPublic(null));
        });

      fetchLocations()
        .then((locations) => {
          dispatch(setLocationData(locations));
          // check if stored location is valid
          const storedLocation: string =
            (retrieveState(StorageTypes.CurrentLocation) as string) ?? '#';
          const isValidLoc = locations?.locations?.find(
            (locDetail) => locDetail.id === storedLocation
          );

          const rootLocName = locations.locations?.find((loc) => loc.id === '#')?.name;
          if (rootLocName) {
            posthog.capture('Root Location', { rootLocName });
            if (allTFLLocations.includes(rootLocName)) setShowSubscriptionBanner(true);
          }

          if (!isValidLoc) {
            dispatch(setCurrentLocation('#'));
          }
        })
        .catch(() => {
          dispatch(setLocationData(null));
        });

      fetchAllSensorsInLocation('#', ['name', 'location'])
        .then((sensors) => {
          const sensorProps = sensors.sensors ?? [];
          dispatch(setSensorsById(sensorProps as SensorLatest[]));
        })
        .catch(() => null);

      fetchAvailableVars()
        .then((vars) => {
          dispatch(setAvailableVars(vars));
        })
        // keep all vars for error
        .catch(() => dispatch(setAvailableVars([])));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userName, dispatch]);

  return (
    <Router>
      <>
        {showSubscriptionBanner && (
          <Box className={classes.subscriptionBanner}>
            <Typography variant="h6">Subscription is overdue</Typography>
            <CloseIcon
              color="error"
              onClick={() => setShowSubscriptionBanner(false)}
              style={{ cursor: 'pointer', color: themeProps.colors.white, textAlign: 'end' }}
            />
          </Box>
        )}
        <MobileViewDrawer />
        <DesktopViewDrawer />
        <div
          className={`${classes.shell} no-print`}
          style={{ minHeight: window.innerHeight ? window.innerHeight : '100vh' }}
        >
          <Routes>
            {routes.map((route) => (
              <Route
                path={route.url}
                key={route.id}
                element={
                  <MainWrapper
                    routeComponent={<route.component />}
                    helmetTitle={route.helmetTitle}
                  />
                }
              />
            ))}
          </Routes>
        </div>
      </>
    </Router>
  );
}

export default Shell;
